import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import useOsDark from "@/OS/hooks/useOsDark"
import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const getPortalContainer = () => {
  if (typeof document === "undefined") {
    return undefined
  }

  return (
    (document.fullscreenElement as HTMLElement | null) ??
    (document.querySelector(".mapleos-root") as HTMLElement | null) ??
    document.body ??
    undefined
  )
}

const usePopoverPortalTheme = () => {
  const isDark = useOsDark()
  const isMapleOS = typeof document !== "undefined" && Boolean(document.querySelector(".mapleos-root"))

  return {
    container: getPortalContainer(),
    themedClassName: cn(isMapleOS && "mapleos-root", isMapleOS && isDark && "dark"),
  }
}

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  const { container, themedClassName } = usePopoverPortalTheme()

  return (
    <PopoverPrimitive.Portal container={container}>
      <div className={themedClassName}>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-[9999] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className
          )}
          {...props}
        />
      </div>
    </PopoverPrimitive.Portal>
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
